import { createContext, useContext } from 'react'

/**
 * Whether this screen is the page, or a tab body inside one.
 *
 * Six screens now render in two places. Policies, Dictionaries and Assistant are one screen with
 * three tabs; Devices, Deployment and Trust are another. Their feature folders were not moved and
 * their data fetching was not touched - that re-parenting is what made the whole IA change
 * affordable - but a tab body must not render a second `<h1>`, because the host already rendered the
 * page's one.
 *
 * A context rather than a prop threaded through each page, so a screen that is added to a tab host
 * later needs no change at all, and a screen used in both places cannot be wrong in one of them.
 */
const TabBodyContext = createContext(false)

export function TabBody({ children }: { children: React.ReactNode }) {
  return <TabBodyContext.Provider value={true}>{children}</TabBodyContext.Provider>
}

/**
 * The screen's title, at whichever level it is actually at.
 *
 * `layout.spec.ts` asserts exactly one level-1 heading per route under strict mode, so getting this
 * wrong fails the build rather than quietly giving a screen reader two page titles to choose from.
 */
export function PageTitle({ children, ...rest }: React.HTMLAttributes<HTMLHeadingElement>) {
  const insideTab = useContext(TabBodyContext)
  return insideTab ? <h2 {...rest}>{children}</h2> : <h1 {...rest}>{children}</h1>
}
